import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { DuelMissionTargetPng } from '../../../assets/duels';
import type { SvaraDailyMissionDto } from '../../../services/api';
import { missionsHubRequest } from '../../../store/missions/missionsActions';
import { selectMissionsList, selectMissionsResetAt } from '../../../store/missions/missionsReducer';
import { eventColors } from '../Event/eventTheme';
import { duelText } from './duelsText';
import { DuelPanel, DuelProgressBar, duelTextStyles } from './DuelsUi';

const MISSION_CYCLE_MS = 24 * 60 * 60_000;
const MAX_PIPS = 10;
const DUEL_EVENT_TYPES = ['DuelPlayed', 'DuelWon'];

const inCurrentCycle = (settledAt: string, resetAt: string | null) => {
	if (!resetAt) return false;
	const settled = new Date(settledAt).getTime();
	const reset = new Date(resetAt).getTime();
	return settled >= reset - MISSION_CYCLE_MS && settled < reset;
};

const MissionRow = ({
	mission,
	width,
	scale,
}: {
	mission: SvaraDailyMissionDto;
	width: number;
	scale: number;
}) => {
	const target = Math.max(1, mission.completionCount);
	const done = Math.min(mission.currentCount, target);
	const trackWidth = width * 0.42;
	return (
		<View style={[styles.row, { columnGap: 8 * scale }]}>
			<Image
				source={DuelMissionTargetPng}
				style={{ width: 22 * scale, height: 22 * scale }}
				resizeMode='contain'
			/>
			<Text numberOfLines={2} style={[duelTextStyles.body, styles.flex, { fontSize: 12 * scale }]}>
				{mission.completed ? <Text style={styles.done}>✓ </Text> : null}
				{mission.title}
			</Text>
			<View style={[styles.end, { rowGap: 2 * scale }]}>
				<Text style={[duelTextStyles.cream, { fontSize: 10 * scale }]}>{`${done}/${target}`}</Text>
				{target > MAX_PIPS ? (
					<DuelProgressBar value={done / target} width={trackWidth} scale={scale} />
				) : (
					<View style={[styles.row, { columnGap: 3 * scale }]}>
						{Array.from({ length: target }, (_, index) => (
							<View
								key={index}
								style={[
									styles.pip,
									{
										width: (trackWidth - 3 * scale * (target - 1)) / target,
										height: 8 * scale,
										borderRadius: 4 * scale,
									},
									index < done ? styles.pipDone : null,
								]}
							/>
						))}
					</View>
				)}
			</View>
		</View>
	);
};

type Props = { settledAt: string | null; width: number; scale: number };

export const DuelMissionProgress = ({ settledAt, width, scale }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const missions = useSelector(selectMissionsList);
	const resetAt = useSelector(selectMissionsResetAt);

	useFocusEffect(
		useCallback(() => {
			dispatch(missionsHubRequest());
		}, [dispatch]),
	);

	useEffect(() => {
		if (settledAt) dispatch(missionsHubRequest());
	}, [dispatch, settledAt]);

	if (!settledAt || !inCurrentCycle(settledAt, resetAt)) return null;
	const duelMissions = (missions ?? []).filter(mission =>
		DUEL_EVENT_TYPES.includes(mission.eventType),
	);
	if (!duelMissions.length) return null;

	return (
		<DuelPanel scale={scale} width={width}>
			<Text style={[duelTextStyles.title, { fontSize: 16 * scale }]}>
				{duelText(t, 'T_SITE_DUELS_MISSION_PROGRESS')}
			</Text>
			{duelMissions.map(mission => (
				<MissionRow key={mission.id} mission={mission} width={width - 24 * scale} scale={scale} />
			))}
		</DuelPanel>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	end: {
		alignItems: 'flex-end',
	},
	pip: {
		backgroundColor: 'rgba(122,24,24,0.9)',
	},
	pipDone: {
		backgroundColor: eventColors.gold,
	},
	done: {
		color: '#2FE07A',
	},
});
